import { Link } from "react-router-dom";
import { Bed, Car, Maximize, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Listing } from "@/lib/listings";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { listingPath } from "@/lib/slugify";

const formatPrice = (price: number, lang: string) =>
  new Intl.NumberFormat(lang === "en" ? "en-US" : "pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(price);

const formatPricePerM2 = (value: number | null) =>
  value ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(value) + "/m²" : "";

interface ListingCardProps {
  listing: Listing;
}

const ListingCard = ({ listing }: ListingCardProps) => {
  const { t, language } = useLanguage();
  const whatsappMsg = t("listings.whatsappMsg")
    .replace("{title}", listing.title)
    .replace("{price}", formatPrice(listing.price, language));
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;

  const mainImage = listing.images?.[0] || "/placeholder.svg";

  return (
    <div className="group bg-card rounded-lg overflow-hidden shadow-lg border border-border transition-all hover:shadow-xl hover:-translate-y-1">
      <Link to={listingPath(listing)} title={listing.title} className="block">
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={mainImage}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="rounded bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground font-body">
            {t(`type.${listing.property_type}`) || listing.property_type}
          </span>
          {listing.is_featured && (
            <span className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground font-body">
              {t("listings.featured")}
            </span>
          )}
        </div>
        <div className="absolute bottom-3 right-3">
          <span className="rounded bg-primary/90 px-3 py-1.5 text-sm font-bold text-primary-foreground font-body">
            {formatPrice(listing.price, language)}
          </span>
        </div>
      </div>
      </Link>
      <div className="p-5">
        {(listing as any).listing_code && (
          <span className="text-xs font-semibold text-primary font-body mb-1 block">{t("listings.code")}: {(listing as any).listing_code}</span>
        )}
        <h3 className="font-display text-lg font-bold text-foreground mb-2 line-clamp-2">
          {listing.title}
        </h3>
        <div className="flex items-center gap-1 text-muted-foreground mb-3 font-body text-sm">
          <MapPin className="h-4 w-4" />
          <span>{listing.neighborhood}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 font-body">
          <span className="flex items-center gap-1">
            <Bed className="h-4 w-4" /> {listing.bedrooms} {listing.suites ? `(${listing.suites} ${t("listings.suites")})` : ""}
          </span>
          <span className="flex items-center gap-1">
            <Car className="h-4 w-4" /> {listing.garage_spots || 0}
          </span>
          <span className="flex items-center gap-1">
            <Maximize className="h-4 w-4" /> {listing.area_m2}m²
          </span>
        </div>
        {listing.price_per_m2 && (
          <p className="text-xs text-muted-foreground font-body mb-4">
            {formatPricePerM2(listing.price_per_m2)}
          </p>
        )}
        <Button variant="whatsapp" className="w-full" asChild>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" title={`Agendar visita: ${listing.title}`}>
            <Phone className="h-4 w-4" />
            {t("listings.whatsapp")}
          </a>
        </Button>
      </div>
    </div>
  );
};

interface ListingsGridProps {
  listings: Listing[];
  isLoading?: boolean;
}

const ListingsGrid = ({ listings, isLoading }: ListingsGridProps) => {
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <section id="imoveis" className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-display font-bold text-foreground text-center mb-10">
          {t("listings.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-lg overflow-hidden shadow-lg animate-pulse">
              <div className="aspect-[4/3] bg-muted" />
              <div className="p-5 space-y-3">
                <div className="h-5 bg-muted rounded w-3/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-10 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (listings.length === 0) {
    return (
      <section id="imoveis" className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-display font-bold text-foreground mb-4">
          {t("listings.title")}
        </h2>
        <p className="text-muted-foreground font-body text-lg">
          {t("listings.empty")}
        </p>
      </section>
    );
  }

  return (
    <section id="imoveis" className="container mx-auto px-4 py-16">
      <h2 className="text-3xl font-display font-bold text-foreground text-center mb-3">
        {t("listings.title")}
      </h2>
      <p className="text-muted-foreground font-body text-center mb-10">
        {listings.length} {t("listings.count")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {listings.map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>
    </section>
  );
};

export default ListingsGrid;
